import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { SurveyData } from '../types';

interface HealthTrendsProps {
  surveyData: SurveyData[];
}

const HealthTrends: React.FC<HealthTrendsProps> = ({ surveyData }) => {
  const chartData = surveyData.slice(-14).map(survey => ({
    date: new Date(survey.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    stress: survey.stressLevel,
    energy: survey.energyLevel,
    sleep: survey.sleepQuality,
    mood: survey.moodScore,
    activity: survey.physicalActivity,
    nutrition: survey.nutritionScore,
    hydration: survey.hydrationLevel,
    focus: survey.focusLevel,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ 
          backgroundColor: 'rgba(26, 26, 26, 0.95)', 
          border: '1px solid #00ff88', 
          borderRadius: 2, 
          p: 2 
        }}>
          <Typography variant="body2" sx={{ color: '#00ff88', mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block' }}>
              {entry.name}: {entry.value}%
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
          Health Trends (Last 14 Days)
        </Typography>
        
        {chartData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No data available yet. Complete your first survey to see trends.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ height: 300 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                  Overall Health Metrics
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#fff"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#fff"
                      fontSize={12}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="energy"
                      stackId="1"
                      stroke="#ffd700"
                      fill="#ffd700"
                      fillOpacity={0.3}
                    />
                    <Area
                      type="monotone"
                      dataKey="sleep"
                      stackId="1"
                      stroke="#4fc3f7"
                      fill="#4fc3f7"
                      fillOpacity={0.3}
                    />
                    <Area
                      type="monotone"
                      dataKey="mood"
                      stackId="1"
                      stroke="#00ff88"
                      fill="#00ff88"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 250 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                  Stress vs Focus
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#fff"
                      fontSize={10}
                    />
                    <YAxis 
                      stroke="#fff"
                      fontSize={10}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="stress"
                      stroke="#ff6b6b"
                      strokeWidth={2}
                      dot={{ fill: '#ff6b6b', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="focus"
                      stroke="#00ff88"
                      strokeWidth={2}
                      dot={{ fill: '#00ff88', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 250 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                  Activity & Nutrition
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#fff"
                      fontSize={10}
                    />
                    <YAxis 
                      stroke="#fff"
                      fontSize={10}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="activity"
                      stroke="#ff6b6b"
                      strokeWidth={2}
                      dot={{ fill: '#ff6b6b', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="nutrition"
                      stroke="#4fc3f7"
                      strokeWidth={2}
                      dot={{ fill: '#4fc3f7', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hydration"
                      stroke="#00ff88"
                      strokeWidth={2}
                      dot={{ fill: '#00ff88', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default HealthTrends;
